<template>
  <a-modal
    :title="isEditing ? '编辑回复内容' : '添加回复内容'"
    v-model:visible="visibleValue"
    :confirm-loading="isSubmitting"
    @cancel="handleCancel"
    :width="500"
    class="response-register-modal"
    :destroyOnClose="true"
    :maskClosable="false"
    :keyboard="true"
    :getPopupContainer="getPopupContainer"
    :footer="null"
  >
    <div class="modal-content">
      <a-form 
        :model="formState" 
        :rules="rules" 
        ref="formRef" 
        layout="vertical"
      >
        <a-form-item label="回复类型" name="responseType">
          <a-radio-group v-model:value="formState.responseType" :disabled="isEditing">
            <a-radio value="TEXT">文本</a-radio>
            <!-- 추후 지원 예정인 기능들
            <a-radio value="LINK">链接</a-radio>
            <a-radio value="IMAGE">图片</a-radio>
            <a-radio value="CARD">卡片</a-radio>
            -->
          </a-radio-group>
        </a-form-item>
        
        <a-form-item label="回复内容" name="content">
          <a-textarea 
            v-model:value="formState.content" 
            placeholder="请输入回复内容" 
            :rows="4" 
            :maxLength="1000"
          />
        </a-form-item>
        
        <!-- LINK 타입 지원 시 활성화 예정
        <a-form-item 
          v-if="formState.responseType === 'LINK'" 
          label="链接地址" 
          name="url"
        >
          <a-input 
            v-model:value="formState.url" 
            placeholder="请输入链接地址"
          />
        </a-form-item>
        -->
        
        <a-form-item label="状态" name="isActive">
          <a-switch v-model:checked="formState.isActive" />
          <span style="margin-left: 8px;">{{ formState.isActive ? '启用' : '禁用' }}</span>
        </a-form-item>
        
        <div class="modal-footer">
          <a-button @click="handleCancel">取消</a-button>
          <a-button type="primary" :loading="isSubmitting" @click="handleSubmit">
            确定
          </a-button>
        </div>
      </a-form>
    </div>
  </a-modal>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue';

export default {
  name: 'ResponseRegisterModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    keywordId: {
      type: String,
      default: ''
    },
    response: {
      type: Object,
      default: () => ({
        _id: null,
        responseType: 'TEXT',
        content: '',
        url: '',
        isActive: true
      })
    },
    isSubmitting: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'update:visible',
    'cancel',
    'submit',
    'create',
    'update'
  ],
  setup(props, { emit }) {
    const formRef = ref(null);
    
    // 폼 상태
    const formState = reactive({
      responseType: 'TEXT',
      content: '',
      url: '',
      isActive: true
    });
    
    // 양방향 바인딩을 위한 computed 속성
    const visibleValue = computed({
      get: () => props.visible,
      set: (val) => {
        emit('update:visible', val);
      }
    });
    
    // 폼 검증 규칙
    const rules = {
      responseType: [
        { required: true, message: '请选择回复类型', trigger: 'change' }
      ],
      content: [
        { required: true, message: '请输入回复内容', trigger: 'blur' },
        { min: 1, max: 1000, message: '回复内容长度应为1-1000个字符', trigger: 'blur' }
      ],
      url: [
        { 
          required: true, 
          message: '请输入链接地址', 
          trigger: 'blur',
          validator: (rule, value) => {
            if (formState.responseType === 'LINK' && !value) {
              return Promise.reject('链接类型回复必须包含链接地址');
            }
            return Promise.resolve();
          }
        },
        { 
          pattern: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/, 
          message: '请输入有效的链接地址', 
          trigger: 'blur',
          validator: (rule, value) => {
            if (formState.responseType === 'LINK' && value && !rule.pattern.test(value)) {
              return Promise.reject('请输入有效的链接地址');
            }
            return Promise.resolve();
          }
        }
      ]
    };
    
    // 폼 초기화
    const resetForm = () => {
      formState.responseType = 'TEXT'; // 현재는 TEXT 타입만 지원
      formState.content = '';
      // formState.url = ''; // 링크 타입 지원 시 활성화 예정
      formState.isActive = true;
      
      if (formRef.value) {
        formRef.value.resetFields();
      }
    };
    
    // 모달 컨테이너 설정
    const getPopupContainer = () => {
      return document.body;
    };
    
    // 응답 데이터가 변경되면 폼 상태 업데이트
    watch(() => props.response, (newResponse) => {
      if (newResponse) {
        formState.responseType = newResponse.responseType || 'TEXT';
        formState.content = newResponse.content || '';
        // formState.url = newResponse.url || ''; // 링크 타입 지원 시 활성화 예정
        formState.isActive = newResponse.isActive === undefined ? true : newResponse.isActive;
      }
    }, { immediate: true, deep: true });
    
    // visible prop 변화 감시
    watch(() => props.visible, (newVal) => {
      if (!newVal) {
        resetForm();
      }
    });
    
    // 모달 취소 핸들러
    const handleCancel = () => {
      emit('update:visible', false);
      emit('cancel');
    };
    
    // 모달 제출 핸들러
    const handleSubmit = async () => {
      try {
        await formRef.value.validate();
        
        if (props.isEditing) {
          // 편집 모드 - 업데이트 요청
          const updateData = {
            responseId: props.response._id,
            content: formState.content,
            isActive: formState.isActive
          };
          
          // 링크 타입일 경우 URL 추가
          /* 링크 타입 지원 시 활성화 예정
          if (formState.responseType === 'LINK' && formState.url) {
            updateData.url = formState.url;
          }
          */
          
          // 부모 컴포넌트에 업데이트 이벤트 발생
          emit('update', updateData);
        } else {
          // 추가 모드 - 생성 요청
          const createData = {
            keywordId: props.keywordId,
            responseType: formState.responseType,
            content: formState.content
          };
          
          // 링크 타입일 경우 URL 추가
          /* 링크 타입 지원 시 활성화 예정
          if (formState.responseType === 'LINK' && formState.url) {
            createData.url = formState.url;
          }
          */
          
          // 부모 컴포넌트에 생성 이벤트 발생
          emit('create', createData);
        }
        
        emit('submit');
      } catch (error) {
        console.error('Form validation error:', error);
      }
    };
    
    return {
      formRef,
      formState,
      rules,
      visibleValue,
      getPopupContainer,
      handleCancel,
      handleSubmit
    };
  }
};
</script>

<style lang="scss" scoped>
.response-register-modal {
  :deep(.ant-modal-content) {
    border-radius: 8px;
    overflow: hidden;
    
    .ant-modal-header {
      background-color: #f8f9fa;
      padding: 16px 24px;
      border-bottom: 1px solid #f0f0f0;
      
      .ant-modal-title {
        font-size: 18px;
        font-weight: 600;
        color: #1f2937;
      }
    }
    
    .ant-modal-body {
      padding: 0;
    }
  }
  
  .modal-content {
    border: none;
    padding: 24px;
    
    .modal-footer {
      margin-top: 24px;
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      
      .ant-btn {
        height: 36px;
        font-size: 14px;
        border-radius: 4px;
        padding: 0 16px;
      }
    }
  }
}

:deep(.ant-form-item-label) {
  padding-bottom: 4px;
  
  label {
    font-weight: 500;
    color: #4b5563;
    font-size: 14px;
  }
}

:deep(.ant-radio-wrapper) {
  margin-right: 16px;
}

:deep(.ant-input), :deep(.ant-textarea) {
  border-radius: 4px;
  border-color: #d1d5db;
  
  &:hover, &:focus {
    border-color: #3b82f6;
  }
}

// 확인 팝업 버튼을 가로로 배치하기 위한 강화된 스타일
:deep(.ant-popconfirm-buttons) {
  display: flex !important;
  flex-direction: row !important;
  justify-content: flex-end !important;
  
  .ant-btn {
    margin-left: 8px !important;
    margin-top: 0 !important;
    
    &:first-child {
      margin-left: 0 !important;
    }
  }
}
</style>
